import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { changeDeviceView, safelyDetectChanges } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { MapMarkService } from 'src/app/components/map/map-marker.service';
import { LocationObject, Viewport } from '../../../../swagger-api';
import { FormFieldSate } from '../form-field.state';
import { ViewportService } from '../viewport-form-field/viewport-service.service';
import { PreparedLocation } from './geocoding/geocoding.helper';
import { GeocodingService } from './geocoding/geocoding.service';
import { LocationInputAutocompleteService } from './geocoding/location-input-autocomplete.service';

@Component({
  selector: 'app-location-form-field',
  templateUrl: './location-form-field.component.html',
  styleUrls: ['./location-form-field.component.scss'],
  providers: [LocationInputAutocompleteService],
})
export class LocationFormFieldComponent extends FormFieldSate implements AfterViewInit, OnDestroy {
  public isMobileView = false;

  @ViewChild('geocomplete', { static: false }) protected searchElementRef: ElementRef<HTMLInputElement>;

  protected viewportService = inject(ViewportService);
  protected geocodingService = inject(GeocodingService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected mapMarkService = inject(MapMarkService);
  protected autocompleteService = inject(LocationInputAutocompleteService);

  @HostListener('window:resize')
  public resizeWindow(): void {
    this.isMobileView = changeDeviceView(AppValues.DEFAULT_WIDTH_FOR_MOBILE);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    this.subscribeToLocationChanges();
    this.isMobileView = changeDeviceView(AppValues.DEFAULT_WIDTH_FOR_MOBILE);
    this.setupValidators();
  }

  public ngAfterViewInit(): void {
    this.autocompleteService.placesAutocompleteInit(
      this.searchElementRef,
      this.fieldAttribute.backendAttribute.value_class_front,
    );

    this.setupNativeElementValue();
    safelyDetectChanges(this.changeDetectorRef);
  }

  public clearField(): void {
    this.control.setValue(null);
    this.control.markAsDirty();
    this.mapMarkService.clearMapMarker(true);
  }

  protected setupValidators(): void {
    // Simply empty
  }

  protected setupNativeElementValue(): void {
    // Simply empty
  }

  protected updateValue(): void {
    // Simply empty
  }

  protected updateControlValue(_location: LocationObject): void {
    // Simply empty
  }

  protected updateViewPortIfNecessary(_viewPort: Viewport): void {
    // Simply empty
  }

  protected runAdditionalValidationIfNecessary(_preparedLocationComponents: PreparedLocation): void {
    // Simply empty
  }

  protected override onNullControlValue(): void {
    this.searchElementRef.nativeElement.value = '';
  }

  protected override onDefinedControlValue(value: string): void {
    this.searchElementRef.nativeElement.value = value;
    this.control.setValue(value);
  }

  private processPlaceResult(value: google.maps.places.PlaceResult): void {
    this.autocompleteService.processPlaceResult(value, this.fieldAttribute.backendAttribute.value_class_front);
  }

  private updateProfileLocation(preparedLocationComponents: PreparedLocation): void {
    let countryAlpha2 = '';
    let countryFullName = '';
    let cityName = '';
    let regionName = '';
    let subregionName = '';
    let provinceName = '';

    preparedLocationComponents.address_components.forEach((data: google.maps.GeocoderAddressComponent) => {
      if (data.types[0] === 'country') {
        countryFullName = data.long_name;
        countryAlpha2 = data.short_name;
      }

      if (data.types[0] === 'locality' || data.types[0] === 'postal_town' || data.types[0] === 'colloquial_area') {
        cityName = data.long_name;
      }

      if (data.types[0] === 'administrative_area_level_1') {
        regionName = data.long_name;
      }

      if (data.types[0] === 'administrative_area_level_2') {
        subregionName = data.long_name;
      }

      if (data.types[0] === 'administrative_area_level_3') {
        provinceName = data.long_name;
      }
    });

    const streetName: string = preparedLocationComponents.street;

    this.updateControlValue({
      country: {
        alpha2: countryAlpha2,
        full_name: countryFullName,
      },
      region: regionName,
      subregion: subregionName,
      province: provinceName,
      city: cityName,
      street: streetName,
      center: preparedLocationComponents.center,
    });

    this.updateViewPortIfNecessary(preparedLocationComponents.viewport);

    this.runAdditionalValidationIfNecessary(preparedLocationComponents);

    this.updateValue();
  }

  private subscribeToLocationChanges(): void {
    this.subscriptions.push(
      this.autocompleteService.autocompletePlaceChanged.subscribe((response: google.maps.places.PlaceResult) => {
        this.processPlaceResult(response);
      }),

      this.autocompleteService.placeResultParsed.subscribe((preparedLocationComponents: PreparedLocation) => {
        this.updateProfileLocation(preparedLocationComponents);
      }),

      this.geocodingService.locationByCoordsGeocoded.subscribe((preparedLocationComponents: PreparedLocation) => {
        this.updateProfileLocation(preparedLocationComponents);
        this.geocodingService.onLocationInputValueUpdated();
      }),
    );
  }
}
